import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  approveAdminKyc,
  getAdminKyc,
  listAdminKyc,
  rejectAdminKyc,
  type ListAdminKycParams,
} from '@shared/api/admin';
import { getTokenStore } from '@shared/api/token-store';

export const ADMIN_KYC_LIST_KEY = ['admin', 'kyc', 'list'] as const;
export const ADMIN_KYC_ONE_KEY = (id: string) => ['admin', 'kyc', 'one', id] as const;

export function useAdminKycList(params: ListAdminKycParams = {}) {
  return useQuery({
    queryKey: [...ADMIN_KYC_LIST_KEY, params],
    queryFn: () => listAdminKyc(params),
    enabled: !!getTokenStore().getAccessToken(),
    staleTime: 15_000,
  });
}

export function useAdminKycOne(id: string | undefined) {
  return useQuery({
    queryKey: id ? ADMIN_KYC_ONE_KEY(id) : ['admin', 'kyc', 'one', 'disabled'],
    queryFn: () => getAdminKyc(id as string),
    enabled: !!id && !!getTokenStore().getAccessToken(),
    staleTime: 15_000,
  });
}

export function useApproveKyc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => approveAdminKyc(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'kyc'] });
      toast.success('KYC application approved successfully');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to approve KYC');
    },
  });
}

export function useRejectKyc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      rejectAdminKyc(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'kyc'] });
      toast.success('KYC application rejected');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to reject KYC');
    },
  });
}
